<template>
    <div>
        <div class="mb-6">
            <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {{ t("ai.providers.title") }}
            </h1>
            <p class="mt-1 text-gray-600 dark:text-gray-400">
                {{ t("ai.providers.description") }}
            </p>
        </div>

        <div
            v-if="failoverSource"
            class="mb-6 rounded-lg border p-4"
            :class="
                failoverSource === 'env'
                    ? 'border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/20'
                    : 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20'
            "
        >
            <div class="flex items-start">
                <Info
                    class="mr-3 mt-0.5 h-5 w-5 shrink-0"
                    :class="
                        failoverSource === 'env'
                            ? 'text-yellow-600 dark:text-yellow-400'
                            : 'text-blue-600 dark:text-blue-400'
                    "
                />
                <p
                    class="text-sm"
                    :class="
                        failoverSource === 'env'
                            ? 'text-yellow-800 dark:text-yellow-300'
                            : 'text-blue-800 dark:text-blue-300'
                    "
                >
                    {{
                        failoverSource === "env"
                            ? t("ai.providers.failoverEnvBanner", {
                                  order: failoverOrder.join(" → "),
                              })
                            : t("ai.providers.failoverBanner", {
                                  order: failoverOrder.join(" → "),
                              })
                    }}
                </p>
            </div>
        </div>

        <div class="mb-6 flex justify-end gap-3">
            <button
                class="inline-flex items-center rounded-lg bg-green-600 px-4 py-2 text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="testingAll"
                @click="testAllProviders"
            >
                <Loader2 v-if="testingAll" class="mr-2 h-4 w-4 animate-spin" />
                <Zap v-else class="mr-2 h-5 w-5" />
                {{
                    testingAll
                        ? t("ai.providers.testingAll")
                        : t("ai.providers.testAll")
                }}
            </button>
        </div>

        <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div
                v-for="(provider, key) in providers"
                :key="key"
                class="rounded-lg border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-800"
            >
                <div class="mb-4 flex items-start justify-between">
                    <div class="flex items-center space-x-3">
                        <div
                            class="flex h-12 w-12 items-center justify-center rounded-lg"
                            :class="getProviderColor(key)"
                        >
                            <component
                                :is="getProviderIcon(key)"
                                class="h-6 w-6"
                            />
                        </div>
                        <div>
                            <h3
                                class="text-lg font-semibold text-gray-900 dark:text-white"
                            >
                                {{ provider.name }}
                            </h3>
                            <p
                                v-if="provider.model"
                                class="mt-0.5 text-xs text-gray-500 dark:text-gray-400"
                            >
                                {{
                                    t("ai.providers.modelLabelValue", {
                                        model: provider.model,
                                    })
                                }}
                            </p>
                            <div class="mt-1 flex items-center space-x-2">
                                <span
                                    class="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                                    :class="
                                        provider.enabled
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                    "
                                >
                                    {{
                                        provider.enabled
                                            ? t("ai.providers.enabled")
                                            : t("ai.providers.disabled")
                                    }}
                                </span>
                                <span
                                    v-if="!provider.has_key"
                                    class="inline-flex items-center rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
                                >
                                    {{ t("ai.providers.noApiKey") }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <label
                        class="relative inline-flex items-center"
                        :class="
                            isChainActive
                                ? 'cursor-not-allowed opacity-50'
                                : 'cursor-pointer'
                        "
                        :title="
                            isChainActive
                                ? t('ai.providers.failoverToggleHint')
                                : undefined
                        "
                    >
                        <input
                            v-model="provider.enabled"
                            type="checkbox"
                            class="peer sr-only"
                            :disabled="
                                provider.disabledToggle ||
                                updatingProvider === key ||
                                isChainActive
                            "
                            @change="toggleProvider(key, provider.enabled)"
                        />
                        <div
                            class="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-4 peer-focus:ring-primary-300 peer-focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-primary-800"
                        ></div>
                    </label>
                </div>

                <div class="border-t border-gray-200 pt-4 dark:border-gray-700">
                    <div class="mb-4 space-y-3">
                        <div>
                            <label
                                class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400"
                            >
                                {{ t("ai.providers.apiKeyLabel") }}
                            </label>
                            <div class="flex gap-2">
                                <input
                                    v-model="providerForms[key].api_key"
                                    :type="showKey[key] ? 'text' : 'password'"
                                    :placeholder="
                                        provider.has_key
                                            ? t('ai.providers.keyStored')
                                            : t('ai.providers.keyPlaceholder')
                                    "
                                    autocomplete="off"
                                    class="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                />
                                <button
                                    v-if="providerForms[key].api_key"
                                    type="button"
                                    class="px-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                    :title="
                                        showKey[key]
                                            ? t('ai.providers.hideKey')
                                            : t('ai.providers.showKey')
                                    "
                                    @click="showKey[key] = !showKey[key]"
                                >
                                    <component
                                        :is="showKey[key] ? EyeOff : Eye"
                                        class="h-4 w-4"
                                    />
                                </button>
                            </div>
                        </div>

                        <div>
                            <label
                                class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400"
                            >
                                {{ t("ai.providers.modelLabel") }}
                            </label>
                            <input
                                v-model="providerForms[key].model"
                                type="text"
                                class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div v-if="key === 'ollama'">
                            <label
                                class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400"
                            >
                                {{ t("ai.providers.baseUrlLabel") }}
                            </label>
                            <input
                                v-model="providerForms[key].base_url"
                                type="text"
                                class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <button
                            class="inline-flex w-full items-center justify-center rounded-lg bg-primary-600 px-4 py-2 text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                            :disabled="savingProvider === key"
                            @click="saveProvider(key)"
                        >
                            <Loader2
                                v-if="savingProvider === key"
                                class="mr-2 h-4 w-4 animate-spin"
                            />
                            {{
                                savingProvider === key
                                    ? t("common.saving")
                                    : t("ai.providers.saveConfiguration")
                            }}
                        </button>
                    </div>

                    <div
                        class="mb-3 flex items-center justify-between border-t border-gray-200 pt-3 dark:border-gray-700"
                    >
                        <span class="text-sm text-gray-600 dark:text-gray-400">
                            {{ t("ai.providers.connectionStatus") }}
                        </span>
                        <span
                            v-if="provider.tested"
                            class="text-sm font-medium"
                            :class="
                                provider.testSuccess
                                    ? 'text-green-600 dark:text-green-400'
                                    : 'text-red-600 dark:text-red-400'
                            "
                        >
                            {{
                                provider.testSuccess
                                    ? "✓ " + t("ai.providers.connected")
                                    : "✗ " + t("ai.providers.failed")
                            }}
                        </span>
                        <span
                            v-else
                            class="text-sm text-gray-500 dark:text-gray-500"
                        >
                            {{ t("ai.providers.notTested") }}
                        </span>
                    </div>

                    <div
                        v-if="
                            provider.tested &&
                            provider.testSuccess &&
                            provider.responseTime
                        "
                        class="mb-3 flex items-center space-x-2"
                    >
                        <span class="text-sm text-gray-600 dark:text-gray-400">
                            {{ t("ai.providers.responseTime") }}:
                        </span>
                        <span
                            class="text-sm font-medium text-gray-900 dark:text-white"
                        >
                            {{ provider.responseTime }}ms
                        </span>
                    </div>

                    <div
                        v-if="
                            provider.tested &&
                            provider.testSuccess &&
                            provider.testResponse
                        "
                        class="mb-3"
                    >
                        <p
                            class="mb-1 text-sm text-gray-600 dark:text-gray-400"
                        >
                            {{ t("ai.providers.response") }}:
                        </p>
                        <p class="text-sm italic text-gray-900 dark:text-white">
                            "{{ provider.testResponse }}"
                        </p>
                    </div>

                    <div
                        v-if="
                            provider.tested &&
                            !provider.testSuccess &&
                            provider.error
                        "
                        class="mb-3"
                    >
                        <p class="text-sm text-red-600 dark:text-red-400">
                            {{ provider.error }}
                        </p>
                    </div>

                    <button
                        class="inline-flex w-full items-center justify-center rounded-lg border border-primary-600 px-4 py-2 text-primary-600 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
                        :disabled="testingProvider === key || !provider.enabled"
                        @click="testProvider(key)"
                    >
                        <Loader2
                            v-if="testingProvider === key"
                            class="mr-2 h-4 w-4 animate-spin"
                        />
                        <FlaskConical v-else class="mr-2 h-4 w-4" />
                        {{
                            testingProvider === key
                                ? t("ai.providers.testing")
                                : t("ai.providers.testConnection")
                        }}
                    </button>

                    <div
                        class="mt-4 border-t border-gray-200 pt-4 dark:border-gray-700"
                    >
                        <div class="mb-2 flex items-center justify-between">
                            <span
                                class="text-xs font-medium text-gray-600 dark:text-gray-400"
                            >
                                {{ t("ai.providers.failoverChain") }}
                            </span>
                            <span
                                v-if="chainPosition(key)"
                                class="inline-flex items-center rounded bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
                            >
                                #{{ chainPosition(key) }}
                            </span>
                        </div>
                        <div class="flex items-center gap-2">
                            <template v-if="chainPosition(key)">
                                <button
                                    type="button"
                                    class="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                    :disabled="
                                        !canManageOrder ||
                                        savingOrder ||
                                        chainPosition(key) === 1
                                    "
                                    :title="t('ai.providers.failoverMoveUp')"
                                    @click="moveInChain(key, -1)"
                                >
                                    <ArrowUp class="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    class="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                    :disabled="
                                        !canManageOrder ||
                                        savingOrder ||
                                        chainPosition(key) ===
                                            failoverOrder.length
                                    "
                                    :title="t('ai.providers.failoverMoveDown')"
                                    @click="moveInChain(key, 1)"
                                >
                                    <ArrowDown class="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    class="rounded-lg border border-red-300 p-2 text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
                                    :disabled="!canManageOrder || savingOrder"
                                    :title="t('ai.providers.failoverRemove')"
                                    @click="removeFromChain(key)"
                                >
                                    <X class="h-4 w-4" />
                                </button>
                            </template>
                            <button
                                v-else
                                type="button"
                                class="inline-flex items-center rounded-lg border border-primary-600 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/20"
                                :disabled="
                                    !canManageOrder ||
                                    savingOrder ||
                                    !canJoinChain(key)
                                "
                                :title="
                                    canJoinChain(key)
                                        ? t('ai.providers.failoverAdd')
                                        : t('ai.providers.failoverNeedsKey')
                                "
                                @click="addToChain(key)"
                            >
                                <Plus class="mr-1 h-4 w-4" />
                                {{ t("ai.providers.failoverAdd") }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import {
    ArrowDown,
    ArrowUp,
    Brain,
    Cloud,
    Eye,
    EyeOff,
    FlaskConical,
    Globe,
    Info,
    Loader2,
    Plus,
    X,
    Zap,
} from "@lucide/vue";
import aiService from "@/api/ai";
import type { AiProvider } from "@/api/ai";
import { useToastStore } from "@/stores/toastStore";

interface ProviderCardState extends AiProvider {
    disabledToggle: boolean;
    tested: boolean;
    testSuccess: boolean;
    responseTime: number | null;
    testResponse: string | null;
    error: string | null;
    icon: unknown;
}

const { t } = useI18n();
const toastStore = useToastStore();

const iconMap: Record<string, unknown> = { Globe, Zap, Cloud, Brain };

const providers = ref<Record<string, ProviderCardState>>({});
const providerForms = ref<
    Record<string, { api_key: string; model: string; base_url: string }>
>({});
const showKey = ref<Record<string, boolean>>({});

const providerDefaults = {
    disabledToggle: false,
    tested: false,
    testSuccess: false,
    responseTime: null,
    testResponse: null,
    error: null,
};

const testingProvider = ref<string | null>(null);
const testingAll = ref(false);
const updatingProvider = ref<string | null>(null);
const savingProvider = ref<string | null>(null);

const failoverOrder = ref<string[]>([]);
const failoverSource = ref<"settings" | "env" | null>(null);
const savingOrder = ref(false);

const isChainActive = computed(() => failoverSource.value !== null);
const canManageOrder = computed(() => failoverSource.value !== "env");

const fetchFailover = async (): Promise<void> => {
    try {
        const failover = await aiService.getFailover();
        failoverOrder.value = failover.order || [];
        failoverSource.value = failover.source;
    } catch (error) {
        console.error("Failed to fetch failover order:", error);
    }
};

const chainPosition = (providerKey: string): number | null => {
    const index = failoverOrder.value.indexOf(providerKey);

    return index === -1 ? null : index + 1;
};

const canJoinChain = (providerKey: string): boolean => {
    const provider = providers.value[providerKey];

    return provider ? !provider.requires_key || provider.has_key : false;
};

const saveFailoverOrder = async (order: string[]): Promise<void> => {
    savingOrder.value = true;

    try {
        const failover = await aiService.updateFailover(order);
        failoverOrder.value = failover.order || [];
        failoverSource.value = failover.source;
        toastStore.success(t("ai.providers.failoverOrderSaved"));
    } catch (error) {
        console.error("Failed to save failover order:", error);
        toastStore.error(
            (error as { response?: { data?: { message?: string } } })?.response
                ?.data?.message || t("ai.providers.failoverSaveFailed"),
        );
    } finally {
        savingOrder.value = false;
    }
};

const addToChain = (providerKey: string): void => {
    saveFailoverOrder([...failoverOrder.value, providerKey]);
};

const removeFromChain = (providerKey: string): void => {
    saveFailoverOrder(failoverOrder.value.filter((key) => key !== providerKey));
};

const moveInChain = (providerKey: string, direction: number): void => {
    const order = [...failoverOrder.value];
    const from = order.indexOf(providerKey);
    const to = from + direction;

    if (from === -1 || to < 0 || to >= order.length) {
        return;
    }

    [order[from], order[to]] = [order[to], order[from]];
    saveFailoverOrder(order);
};

const fetchProviders = async (): Promise<void> => {
    try {
        const data = await aiService.getProviders();
        const result: Record<string, ProviderCardState> = {};
        const forms: Record<
            string,
            { api_key: string; model: string; base_url: string }
        > = {};

        for (const [key, provider] of Object.entries(data)) {
            result[key] = {
                ...providerDefaults,
                ...provider,
                // A provider without credentials cannot be enabled.
                disabledToggle: provider.requires_key && !provider.has_key,
                icon: iconMap.Globe,
            } as ProviderCardState;
            forms[key] = {
                api_key: "",
                model: provider.model || "",
                base_url: provider.base_url || "",
            };
            showKey.value[key] = false;
        }

        providers.value = result;
        providerForms.value = forms;
    } catch (error) {
        console.error("Failed to fetch AI providers:", error);
        toastStore.error(t("ai.providers.fetchFailed"));
    }
};

const saveProvider = async (providerKey: string): Promise<void> => {
    savingProvider.value = providerKey;

    const form = providerForms.value[providerKey];
    const payload: {
        model?: string;
        base_url?: string;
        api_key?: string;
    } = { model: form.model || undefined };

    if (providerKey === "ollama" && form.base_url) {
        payload.base_url = form.base_url;
    }

    if (form.api_key) {
        payload.api_key = form.api_key;
    }

    try {
        const updated = await aiService.updateProvider(providerKey, payload);
        providers.value[providerKey] = {
            ...providers.value[providerKey],
            ...updated,
            disabledToggle: updated.requires_key && !updated.has_key,
        };
        providerForms.value[providerKey].api_key = "";
        toastStore.success(
            t("ai.providers.configurationSaved", {
                name: providers.value[providerKey].name,
            }),
        );
    } catch (error) {
        console.error("Failed to save provider configuration:", error);
        const responseData = (
            error as { response?: { data?: Record<string, unknown> } }
        )?.response?.data;
        const apiKeyError = responseData?.data as
            { api_key?: string[] } | undefined;
        toastStore.error(
            apiKeyError?.api_key?.[0] ||
                (responseData?.message as string | undefined) ||
                t("ai.providers.updateFailed"),
        );
    } finally {
        savingProvider.value = null;
    }
};

const toggleProvider = async (
    providerKey: string,
    enabled: boolean,
): Promise<void> => {
    if (!providers.value[providerKey].has_key) {
        toastStore.error(
            t("ai.providers.configureKey", {
                name: providers.value[providerKey].name,
            }),
        );
        fetchProviders();

        return;
    }

    updatingProvider.value = providerKey;

    try {
        await aiService.updateProvider(providerKey, { enabled });
        toastStore.success(
            enabled
                ? t("ai.providers.providerEnabled", {
                      name: providers.value[providerKey].name,
                  })
                : t("ai.providers.providerDisabled", {
                      name: providers.value[providerKey].name,
                  }),
        );
        await fetchProviders();
    } catch (error) {
        console.error("Failed to toggle provider:", error);
        toastStore.error(
            (error as { response?: { data?: { message?: string } } })?.response
                ?.data?.message || t("ai.providers.updateFailed"),
        );
        fetchProviders();
    } finally {
        updatingProvider.value = null;
    }
};

const testProvider = async (providerKey: string): Promise<void> => {
    testingProvider.value = providerKey;
    providers.value[providerKey].tested = false;

    try {
        const result = await aiService.testProvider(providerKey);
        providers.value[providerKey].tested = true;
        providers.value[providerKey].testSuccess = true;
        providers.value[providerKey].responseTime = result.response_time;
        providers.value[providerKey].testResponse = result.response;
        providers.value[providerKey].model = result.model;
        providers.value[providerKey].error = null;

        toastStore.success(
            t("ai.providers.testSuccess", {
                name: providers.value[providerKey].name,
                time: result.response_time,
            }),
        );
    } catch (error) {
        console.error("Provider test failed:", error);
        providers.value[providerKey].tested = true;
        providers.value[providerKey].testSuccess = false;
        providers.value[providerKey].error =
            (error as { response?: { data?: { message?: string } } })?.response
                ?.data?.message || t("ai.providers.unknownError");

        toastStore.error(
            t("ai.providers.testFailed", {
                name: providers.value[providerKey].name,
                error: providers.value[providerKey].error,
            }),
        );
    } finally {
        testingProvider.value = null;
    }
};

const testAllProviders = async (): Promise<void> => {
    testingAll.value = true;

    // In failover mode the chain, not the legacy enabled flag, decides which
    // providers take part in serving requests.
    const targets = failoverOrder.value.length
        ? failoverOrder.value
        : Object.keys(providers.value).filter(
              (key) => providers.value[key].enabled,
          );

    for (const key of targets) {
        if (providers.value[key]) {
            await testProvider(key);
            await new Promise((resolve) => setTimeout(resolve, 500));
        }
    }

    testingAll.value = false;
};

const getProviderIcon = (providerKey: string): unknown => {
    return providers.value[providerKey].icon;
};

const getProviderColor = (providerKey: string): string => {
    const colors: Record<string, string> = {
        gemini: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
        groq: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
        ollama: "bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400",
    };

    return colors[providerKey] || "bg-gray-100 text-gray-600";
};

onMounted(() => {
    fetchProviders();
    fetchFailover();
});
</script>
